import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { getOrganizationSyncStatus, listOrganizationSyncRuns } from './api'

export const organizationSyncStatusQueryKey = ['admin-organization-sync-status'] as const
export const organizationSyncCompletionQueryKey = [
  'admin-organization-sync-latest-completion',
] as const
export const organizationSyncSettingsQueryKey = ['admin-organization-sync-settings'] as const
export const organizationSyncTargetsQueryKey = ['admin-organization-sync-targets'] as const
export const organizationSyncRunsQueryKey = ['admin-organization-sync-runs'] as const

const organizationDataQueryKeys = [
  ['admin-organization-companies'],
  ['admin-organization-tree'],
  ['admin-organization-member-accounts'],
  ['admin-organization-member-candidates'],
] as const

const organizationSyncResultQueryKeys = [
  organizationSyncSettingsQueryKey,
  organizationSyncRunsQueryKey,
] as const

export function invalidateOrganizationDataQueries(queryClient: QueryClient) {
  return Promise.all(
    organizationDataQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  )
}

export function useOrganizationSyncStatus() {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryFn: getOrganizationSyncStatus,
    queryKey: organizationSyncStatusQueryKey,
    refetchInterval: (query) => (query.state.data?.is_syncing ? 2_000 : 10_000),
  })
  const latestRunQuery = useQuery({
    queryFn: () => listOrganizationSyncRuns(1),
    queryKey: organizationSyncCompletionQueryKey,
    refetchInterval: 10_000,
  })
  const wasSyncingRef = useRef(query.data?.is_syncing ?? false)
  const latestRun = latestRunQuery.data?.[0]
  const completionMarker =
    latestRunQuery.data === undefined
      ? undefined
      : latestRun?.completed_at
        ? `${latestRun.id}:${latestRun.completed_at}`
        : null
  const completionMarkerRef = useRef<string | null | undefined>(completionMarker)

  useEffect(() => {
    const isSyncing = query.data?.is_syncing
    const completedBetweenPolls =
      completionMarkerRef.current !== undefined &&
      completionMarker !== undefined &&
      completionMarker !== null &&
      completionMarkerRef.current !== completionMarker
    if ((wasSyncingRef.current && isSyncing === false) || completedBetweenPolls) {
      void Promise.all([
        invalidateOrganizationDataQueries(queryClient),
        ...organizationSyncResultQueryKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ])
    }
    if (typeof isSyncing === 'boolean') {
      wasSyncingRef.current = isSyncing
    }
    if (completionMarker !== undefined) {
      completionMarkerRef.current = completionMarker
    }
  }, [completionMarker, query.data?.is_syncing, queryClient])

  return query
}
